/**
 * Every place this trip touches — not just the one the tapped slot belongs to.
 *
 * ⚠️ THE BUG THIS EXISTS FOR. On a Seattle→Vancouver→cruise→Anchorage trip, the
 * add sheet offered exactly one city: Vancouver. It took `city` from the LEG,
 * treated it as fixed, and gave the traveler no way to change it — so "add a
 * hotel in Anchorage after the cruise", which is the single most obvious thing
 * that trip needs, was unreachable. On a cruise the cities that matter are
 * mostly NOT leg cities: you fly into the embarkation port and out of the
 * disembarkation port, and everything in between is a ship.
 *
 * So the sheet asks the whole trip where it goes, from four sources, in
 * confidence order:
 *   1. the slot's own city — what the traveler tapped, always first
 *   2. the gap analysis's arrival city — where the unplanned days actually are
 *   3. every flight leg's origin and destination city
 *   4. every city already used by an existing itinerary item — which is how a
 *      port the traveler added by hand becomes an offer next time
 *
 * Free text still wins over all of it: this is a suggestion list, never a
 * whitelist. A traveler who wants Talkeetna types Talkeetna.
 */
import type { ItineraryResponse, StewardGap, TripPlanResponse } from "@/lib/types";

export interface PlaceSuggestion {
  city: string;
  /** Why it is being offered — rendered as a quiet hint beside the name. */
  reason: string;
}

/** Case/whitespace-insensitive identity, so "anchorage" ≠ a second Anchorage. */
function norm(city: string): string {
  return city.trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Build the suggestion list. Order is confidence order, deduped on first win,
 * so the slot's own city stays at the head even if a leg mentions it too.
 */
export function tripPlaces(args: {
  slotCity?: string | null;
  plan?: TripPlanResponse | null;
  itinerary?: ItineraryResponse | null;
  gap?: StewardGap | null;
}): PlaceSuggestion[] {
  const out: PlaceSuggestion[] = [];
  const seen = new Set<string>();

  const push = (raw: string | null | undefined, reason: string) => {
    const city = (raw ?? "").trim();
    if (!city) return;
    const key = norm(city);
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ city, reason });
  };

  push(args.slotCity, "this stop");

  // Where the unplanned days actually are. On a cruise this is the city the
  // flight home leaves from — the one the old code could never offer.
  push(args.gap?.arrivalCity, "your open days");

  /*
   * The PORTS, which are on no flight leg at all. The reference trip docks at
   * Seward and flies out of Anchorage, 2½ hours up the road: without these the
   * traveler can plan the airport city but not the place they physically step
   * off the ship, and the Seward→Anchorage journey stays invisible.
   */
  push(args.gap?.disembark?.port, "where you dock");
  push(args.gap?.embarkPort, "where you sail from");

  for (const leg of args.plan?.legs ?? []) {
    push(leg.originCity, `${leg.origin} departure`);
    push(leg.destinationCity, `${leg.destination} arrival`);
  }

  for (const day of args.itinerary?.days ?? []) {
    for (const seg of day.segments) {
      if (seg.kind === "item") push(seg.city, "already on your trip");
    }
  }

  return out;
}
